// SyncNotApplicableSection — the third place a synced part can land, beside
// Components and Not synced (Alex, 2026-10-11: "everything goes somewhere, or
// 'component' if it's okay, or 'not sync' if it's not okay. Or something like
// 'not applicable'... if it is somewhere, it is not somewhere else").
//
// What lands here is the MODEL's own answer that a part is not priced:
//   excluded — "Exclude from all BOMs" is set on the part in Onshape
//   not a DB item — db_id holds "N/A" or "fam:na"
// It is INFORMATION, not a fault: nothing to fix, no open/fixed lifecycle, no
// warning colour. The section starts collapsed and only says how many.
//
// Reads GET /api/products/:id/sync-accounting, which replans the LAST read from
// the cache — 0 Onshape calls. That route also returns the tally the Sync line
// prints, so the three sections and the line always come from one plan.
//
// Colour-blind safe (RULES 3): the kind is a WORD.

import { useState, useEffect, useCallback } from 'react';
import DataGrid from './DataGrid.jsx';
import SectionTitle from './SectionTitle.jsx';
import ReasonCell from './ReasonCell.jsx';
import { LINK } from '../icons.js';

const KIND_WORD = { excluded: 'excluded from BOM', 'not-db': 'not a DB item' };

const COLS = [
  { key: 'kind', label: 'Why', width: 140, render: row => KIND_WORD[row.kind] || row.kind },
  {
    key: 'name', label: 'Part', width: 210,
    render: row => row.viewHref
      ? <a href={row.viewHref} target="_blank" rel="noopener noreferrer"
          style={{ color: 'var(--th-navy)', textDecoration: 'underline' }}
          title="Open this part in Onshape">{row.name || '(unnamed)'} {LINK}</a>
      : (row.name || '(unnamed)'),
  },
  { key: 'reason', label: 'Detail', width: 360, render: row => <ReasonCell text={row.reason} /> },
  { key: 'qty', label: 'Qty', width: 60, align: 'right', render: row => row.qty ?? '—' },
];

export default function SyncNotApplicableSection({ productId, onshapeRef, refreshToken, topRule = true }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  const load = useCallback(() => {
    fetch(`/api/products/${productId}/sync-accounting`)
      .then(r => (r.ok ? r.json() : { read_at: null }))
      .then(setData)
      .catch(() => setData({ read_at: null }));
  }, [productId]);

  useEffect(() => { setData(null); load(); }, [load, refreshToken]);
  useEffect(() => { setCollapsed(true); }, [productId]);

  if (!onshapeRef) return null;

  const rows = (data?.notApplicable || []).map((r, i) => ({ ...r, id: i + 1 }));
  const parts = data?.tally?.notApplicable;

  return (
    <section style={topRule ? { marginTop: 14, paddingTop: 8, borderTop: '2px solid var(--th-border)' } : undefined}>
      <SectionTitle
        count={data == null ? null : data.read_at ? `${parts ?? 0} part${parts === 1 ? '' : 's'}` : 'not read yet'}
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
      >Not applicable</SectionTitle>
      {!collapsed && (
        <>
          <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)', marginBottom: 4 }}>
            Parts the model says are not priced. Information only: to price one, clear
            "Exclude from all BOMs" or give it a real db_id in Onshape, then sync.
          </div>
          <DataGrid
            rows={rows}
            columns={COLS}
            loading={data == null}
            emptyMessage={data?.read_at ? 'None — every part in the last read is priced or listed in Not synced / TBD.' : 'Not read yet — press Sync.'}
            storageKey="product-sync-not-applicable"
          />
        </>
      )}
    </section>
  );
}
